import { useCallback, useEffect, useRef, useState } from "react";
import { voiceErrorMessage } from "./voice-errors";
import { apiClient } from "@/shared/lib/api-client";
import { API_ENDPOINTS } from "@/shared/constants/api-endpoints";

type Status = "idle" | "requesting" | "recording" | "transcribing";
const LIMIT_SECONDS = 89; // Leave room for the recorder's final encoded frame.
const MAX_BYTES = 20 * 1024 * 1024;

/** A recording belongs to one question. Cancellation invalidates late mic
 * permissions and HTTP responses, and always releases the physical mic. */
export function useVoiceAnswer(onTranscript: (text: string) => void) {
  const [status, setStatus] = useState<Status>("idle");
  const [seconds, setSeconds] = useState(0);
  const [audioLevels, setAudioLevels] = useState<number[]>([]);
  const meterContext = useRef<AudioContext | null>(null);
  const meterTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const [error, setError] = useState("");
  const generation = useRef(0);
  const inFlight = useRef(false);
  const recorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const request = useRef<AbortController | null>(null);

  const releaseMic = useCallback(() => {
    if (meterTimer.current) clearInterval(meterTimer.current);
    meterTimer.current = null;
    if (meterContext.current) void meterContext.current.close().catch(() => {});
    meterContext.current = null;
    if (timer.current) clearInterval(timer.current);
    timer.current = null;
    stream.current?.getTracks().forEach((track) => track.stop());
    stream.current = null;
  }, []);

  const cancel = useCallback(() => {
    generation.current += 1;
    inFlight.current = false;
    request.current?.abort();
    request.current = null;
    const current = recorder.current;
    recorder.current = null;
    if (current && current.state !== "inactive") current.stop();
    releaseMic();
  }, [releaseMic]);

  useEffect(() => {
    // Stop hidden recording or a pending mic grant, but let an upload finish.
    const hide = () => {
      if (document.hidden && inFlight.current && !request.current) {
        cancel();
        setStatus("idle");
        setError(
          "Recording paused when you left. Tap Record answer to try again.",
        );
      }
    };
    document.addEventListener("visibilitychange", hide);
    return () => {
      document.removeEventListener("visibilitychange", hide);
      cancel();
    };
  }, [cancel]);

  async function start() {
    if (inFlight.current) return;
    if (
      !navigator.mediaDevices?.getUserMedia ||
      typeof MediaRecorder === "undefined"
    ) {
      setError(
        "This browser can't record here. Open this page in Safari or Chrome, or choose Can't talk.",
      );
      return;
    }
    const id = ++generation.current;
    inFlight.current = true;
    setError("");
    setSeconds(0);
    setAudioLevels([]);
    setStatus("requesting");
    try {
      const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (id !== generation.current) {
        mic.getTracks().forEach((track) => track.stop());
        return;
      }
      stream.current = mic;
      // Analyse the same stream locally; never connect it to speakers.
      try {
        const Audio =
          window.AudioContext ??
          (window as unknown as { webkitAudioContext?: typeof AudioContext })
            .webkitAudioContext;
        if (Audio) {
          const context = new Audio();
          meterContext.current = context;
          const analyser = context.createAnalyser();
          analyser.fftSize = 256;
          context.createMediaStreamSource(mic).connect(analyser);
          void context.resume().catch(() => {});
          const samples = new Uint8Array(analyser.fftSize);
          meterTimer.current = setInterval(() => {
            if (id !== generation.current) return;
            analyser.getByteTimeDomainData(samples);
            const rms = Math.sqrt(
              samples.reduce(
                (sum, value) => sum + ((value - 128) / 128) ** 2,
                0,
              ) / samples.length,
            );
            const level = Math.min(1, rms * 5);
            setAudioLevels((previous) => [...previous.slice(-31), level]);
          }, 50);
        }
      } catch {
        // Meter support must not prevent recording on older browsers.
        if (meterContext.current)
          void meterContext.current.close().catch(() => {});
        meterContext.current = null;
      }
      const mimeType = [
        "audio/webm;codecs=opus",
        "audio/mp4",
        "audio/webm",
        "audio/ogg;codecs=opus",
      ].find((type) => MediaRecorder.isTypeSupported(type));
      const active = new MediaRecorder(
        mic,
        mimeType ? { mimeType } : undefined,
      );
      recorder.current = active;
      const chunks: Blob[] = [];
      let bytes = 0;
      active.ondataavailable = (event) => {
        if (id !== generation.current) return;
        bytes += event.data.size;
        if (bytes > MAX_BYTES) {
          cancel();
          setStatus("idle");
          setError("That answer was too large. Try a shorter recording.");
          return;
        }
        if (event.data.size) chunks.push(event.data);
      };
      active.onerror = () => {
        if (id !== generation.current) return;
        cancel();
        setStatus("idle");
        setError("The microphone stopped. Try again, or choose Can't talk.");
      };
      active.onstop = async () => {
        if (id !== generation.current) return;
        releaseMic();
        recorder.current = null;
        const audio = new Blob(chunks, {
          type: active.mimeType || mimeType || "application/octet-stream",
        });
        if (!audio.size) {
          inFlight.current = false;
          setStatus("idle");
          setError("No audio was recorded. Please try again.");
          return;
        }
        setStatus("transcribing");
        const controller = new AbortController();
        request.current = controller;
        const form = new FormData();
        form.append("audio", audio, "answer");
        try {
          const { data } = await apiClient.post<{ result: { text: string } }>(
            API_ENDPOINTS.mission.transcribe,
            form,
            {
              headers: { "Content-Type": "multipart/form-data" },
              signal: controller.signal,
              timeout: 80_000,
            },
          );
          if (id !== generation.current) return;
          const text = data.result?.text?.trim();
          if (!text) throw new Error("empty transcript");
          onTranscript(text);
        } catch (err) {
          if (id !== generation.current) return;
          setError(voiceErrorMessage(err, "transcribe"));
        } finally {
          if (id === generation.current) {
            inFlight.current = false;
            request.current = null;
            setStatus("idle");
          }
        }
      };
      active.start(1000);
      setStatus("recording");
      const began = Date.now();
      timer.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - began) / 1000);
        setSeconds(elapsed);
        if (elapsed >= LIMIT_SECONDS && active.state === "recording")
          active.stop();
      }, 250);
    } catch {
      if (id !== generation.current) return;
      cancel();
      setStatus("idle");
      setError(
        "Microphone access wasn't available. Allow it in your browser and retry, or choose Can't talk.",
      );
    }
  }

  function stop() {
    if (recorder.current?.state === "recording") {
      setStatus("transcribing");
      recorder.current.stop();
    }
  }

  return {
    status,
    seconds,
    audioLevels,
    error,
    start,
    stop,
    busy: status !== "idle",
  };
}
